/**
 * Gamification + learning-science helpers for the learning layer.
 *
 * Everything here is deliberately client-side (localStorage) or computed from
 * the existing learner summary, so the motivational layer needs no backend
 * changes. Two proven ideas power it:
 *   • SM-2 spaced repetition (schedules vocab reviews for long-term retention).
 *   • Points + levels + streaks + a daily goal (variable-reward habit loop).
 */

import type {
  GameId,
  GameScoreDto,
  GuestGameRoundInput,
  GuestProgressInput,
  LearnerLearningSummaryDto,
  LearnerLevel,
} from '@vuganawe/types';

const DAY_MS = 86_400_000;

/* ── XP + levels ─────────────────────────────────────────────────────────── */

/** XP earned from server-tracked progress (lessons completed + quiz quality). */
export function lessonXp(summary: Pick<LearnerLearningSummaryDto, 'completedLessons' | 'averageQuizScore'> | null): number {
  if (!summary) return 0;
  const lessons = summary.completedLessons ?? 0;
  const avgQuiz = summary.averageQuizScore ?? 0;
  return lessons * 30 + Math.round(lessons * (avgQuiz / 100) * 20);
}

export interface LevelInfo {
  level: number;
  /** 0–4, selects a rank title. */
  rankIndex: number;
  xp: number;
  /** XP earned within the current level. */
  intoLevel: number;
  /** XP the current level spans. */
  levelSpan: number;
  toNextLevel: number;
  progressPercent: number;
}

/** Triangular level curve: each level costs a little more than the last. */
export function levelFromXp(xp: number): LevelInfo {
  const total = Math.max(0, Math.floor(xp));
  let level = 1;
  let floor = 0;
  let span = 60;
  while (total >= floor + span) {
    floor += span;
    level += 1;
    span = 60 * level;
  }
  const intoLevel = total - floor;
  return {
    level,
    rankIndex: Math.min(4, Math.floor((level - 1) / 2)),
    xp: total,
    intoLevel,
    levelSpan: span,
    toNextLevel: span - intoLevel,
    progressPercent: span > 0 ? Math.round((intoLevel / span) * 100) : 0,
  };
}

/** All-time XP: server progress + locally-tracked vocabulary practice. */
export function totalXp(summary: LearnerLearningSummaryDto | null): number {
  return lessonXp(summary) + loadReviewXp() + (summary ? 0 : guestGameXp());
}

/* ── Achievement badges (computed from stats — no storage needed) ─────────── */

export interface BadgeDef {
  id: string;
  icon: string;
  earned: boolean;
}

export function computeBadges(
  summary: LearnerLearningSummaryDto | null,
  knownWords: number,
): BadgeDef[] {
  const lessons = summary?.completedLessons ?? 0;
  const total = summary?.totalLessons ?? 0;
  const streak = summary?.streakDays ?? 0;
  const avgQuiz = summary?.averageQuizScore ?? 0;
  return [
    { id: 'firstStep', icon: '🌱', earned: lessons >= 1 },
    { id: 'fiveLessons', icon: '📚', earned: lessons >= 5 },
    { id: 'tenLessons', icon: '🎓', earned: lessons >= 10 },
    { id: 'quizAce', icon: '🎯', earned: avgQuiz >= 90 },
    { id: 'streak3', icon: '🔥', earned: streak >= 3 },
    { id: 'streak7', icon: '⚡', earned: streak >= 7 },
    { id: 'wordCollector', icon: '🗂️', earned: knownWords >= 20 },
    { id: 'finisher', icon: '🏆', earned: total > 0 && lessons >= total },
  ];
}

/* ── SM-2 spaced repetition ──────────────────────────────────────────────── */

export type Grade = 'again' | 'hard' | 'good' | 'easy';

/** SuperMemo-2 quality score (0–5) for each button. */
const GRADE_Q: Record<Grade, number> = { again: 2, hard: 3, good: 4, easy: 5 };

/** XP a review earns, by how well it went. */
export const GRADE_XP: Record<Grade, number> = { again: 2, hard: 6, good: 10, easy: 12 };

export interface SrState {
  /** Successful repetitions in a row. */
  reps: number;
  /** Easiness factor (≥ 1.3). */
  ef: number;
  /** Current interval, in days. */
  interval: number;
  /** When the card is next due (epoch ms). */
  due: number;
  /** When it was last reviewed (epoch ms). */
  last: number;
}

/**
 * Advance a card's schedule with the classic SM-2 algorithm. A failing grade
 * ("again") resets the streak and shows the card again tomorrow; better grades
 * push the next review further out, scaled by the card's easiness.
 */
export function schedule(prev: SrState | undefined, grade: Grade, now = Date.now()): SrState {
  const state: SrState = prev ?? { reps: 0, ef: 2.5, interval: 0, due: now, last: 0 };
  const q = GRADE_Q[grade];
  let { reps, ef, interval } = state;

  if (q < 3) {
    reps = 0;
    interval = 1;
  } else {
    if (reps === 0) interval = 1;
    else if (reps === 1) interval = 6;
    else interval = Math.max(1, Math.round(interval * ef));
    reps += 1;
  }

  ef = ef + (0.1 - (5 - q) * (0.08 + (5 - q) * 0.02));
  if (ef < 1.3) ef = 1.3;

  return { reps, ef, interval, due: now + interval * DAY_MS, last: now };
}

/** A card is due when its scheduled time has passed (new cards are always due). */
export function isDue(state: SrState | undefined, now = Date.now()): boolean {
  return !state || state.due <= now;
}

/* ── localStorage stores ─────────────────────────────────────────────────── */

const SR_KEY = 'vn-vocab-sr';
const REVIEW_XP_KEY = 'vn-vocab-xp';
const DAILY_KEY = 'vn-daily';
const GAME_ROUNDS_KEY = 'vn-game-rounds';
const GUEST_SOURCE_KEY = 'vn-guest-source-id';
const PLACEMENT_LEVEL_KEY = 'vn-placement-level';

/** How many card reviews count as "goal reached" for the day. */
export const DAILY_GOAL = 15;

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* private mode — in-memory only is fine */
  }
}

export function loadSr(): Record<string, SrState> {
  return readJson<Record<string, SrState>>(SR_KEY, {});
}

/** How many words are "learned" — reviewed enough to have stuck (reps ≥ 2). */
export function learnedCount(): number {
  return Object.values(loadSr()).filter((state) => state.reps >= 2).length;
}

export function saveSr(map: Record<string, SrState>): void {
  writeJson(SR_KEY, map);
}

export function loadReviewXp(): number {
  const value = readJson<number>(REVIEW_XP_KEY, 0);
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

export function addReviewXp(amount: number): number {
  const next = loadReviewXp() + amount;
  writeJson(REVIEW_XP_KEY, next);
  return next;
}

/* ── Lesson-step XP (bite-sized progress rewards) ────────────────────────── */

const LEARNING_XP_KEY = 'vn-learning-xp';
const AWARDED_STEPS_KEY = 'vn-awarded-steps';

/** Standard XP amounts for learning progress (kept in sync with the server). */
export const STEP_XP = 10;
export const LESSON_XP = 30;
export const COURSE_XP = 200; // must match COURSE_COMPLETION_XP on the API

/** XP accumulated from completing lesson steps (client-side, motivational). */
export function loadLearningXp(): number {
  const value = readJson<number>(LEARNING_XP_KEY, 0);
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

/**
 * Award XP for completing one lesson step — once per (lesson, step) so it is
 * never double-counted when a learner revisits. Returns the XP gained now.
 */
export function awardStepXp(lessonId: string, stepIndex: number): { gained: number; total: number } {
  const key = `${lessonId}:${stepIndex}`;
  const awarded = readJson<Record<string, true>>(AWARDED_STEPS_KEY, {});
  if (awarded[key]) return { gained: 0, total: loadLearningXp() };
  awarded[key] = true;
  writeJson(AWARDED_STEPS_KEY, awarded);
  const total = loadLearningXp() + STEP_XP;
  writeJson(LEARNING_XP_KEY, total);
  return { gained: STEP_XP, total };
}

export interface DailyProgress {
  date: string;
  reviews: number;
  xp: number;
}

/** Local calendar date key (YYYY-MM-DD) for daily-goal bookkeeping. */
export function todayKey(now = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

export function loadDaily(): DailyProgress {
  const today = todayKey();
  const stored = readJson<DailyProgress>(DAILY_KEY, { date: today, reviews: 0, xp: 0 });
  if (stored.date !== today) return { date: today, reviews: 0, xp: 0 };
  return stored;
}

export function addDaily(reviews: number, xp: number): DailyProgress {
  const current = loadDaily();
  const next: DailyProgress = {
    date: current.date,
    reviews: current.reviews + reviews,
    xp: current.xp + xp,
  };
  writeJson(DAILY_KEY, next);
  return next;
}

/* ── Guest progress import snapshot ─────────────────────────────────────── */

export function getGuestSourceId(): string {
  if (typeof window === 'undefined') return 'server';
  const existing = window.localStorage.getItem(GUEST_SOURCE_KEY);
  if (existing) return existing;
  const generated =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  window.localStorage.setItem(GUEST_SOURCE_KEY, generated);
  return generated;
}

export function loadGuestGameRounds(): GuestGameRoundInput[] {
  return readJson<GuestGameRoundInput[]>(GAME_ROUNDS_KEY, []);
}

export function guestGameScores(): Record<string, GameScoreDto> {
  const scores: Record<string, GameScoreDto> = {};
  for (const round of loadGuestGameRounds()) {
    const current = scores[round.game] ?? { game: round.game, best: 0, plays: 0 };
    current.best = Math.max(current.best, round.score);
    current.plays += 1;
    scores[round.game] = current;
  }
  return scores;
}

export function guestGameXp(): number {
  return loadGuestGameRounds().reduce((sum, round) => {
    const correct = Math.max(0, Math.min(round.correct, round.total));
    return sum + Math.min(25, correct * 2);
  }, 0);
}

export function recordGuestGameRound(round: GuestGameRoundInput): { xpAwarded: number; totalXp: number } {
  const rounds = loadGuestGameRounds();
  const nextRounds = [...rounds, round].slice(-50);
  writeJson(GAME_ROUNDS_KEY, nextRounds);
  const correct = Math.max(0, Math.min(round.correct, round.total));
  const xpAwarded = Math.min(25, correct * 2);
  return { xpAwarded, totalXp: loadReviewXp() + guestGameXp() };
}

export function loadGuestPlacementLevel(): LearnerLevel | undefined {
  if (typeof window === 'undefined') return undefined;
  const value = window.localStorage.getItem(PLACEMENT_LEVEL_KEY);
  return value ? (value as LearnerLevel) : undefined;
}

export function buildGuestProgressSnapshot(): GuestProgressInput | null {
  if (typeof window === 'undefined') return null;
  const sr = loadSr();
  const vocabReviews = Object.entries(sr).map(([word, state]) => ({ word, ...state }));
  const gameRounds = loadGuestGameRounds();
  const reviewXp = loadReviewXp();
  const daily = loadDaily();
  const placementLevel = loadGuestPlacementLevel();
  const hasProgress =
    vocabReviews.length > 0 ||
    gameRounds.length > 0 ||
    reviewXp > 0 ||
    daily.reviews > 0 ||
    Boolean(placementLevel);

  if (!hasProgress) return null;
  return {
    sourceId: getGuestSourceId(),
    reviewXp,
    vocabReviews,
    gameRounds,
    daily,
    placementLevel,
  };
}

export function clearGuestProgressSnapshot(): void {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(SR_KEY);
  window.localStorage.removeItem(REVIEW_XP_KEY);
  window.localStorage.removeItem(DAILY_KEY);
  window.localStorage.removeItem(GAME_ROUNDS_KEY);
  window.localStorage.removeItem(GUEST_SOURCE_KEY);
}
